// Title, hero select, hero creation and room lobby screens.

import { CLASSES, CLASS_IDS, SKILLS } from '../shared/classes';
import { PRIVACY_URL } from '../shared/analytics';
import { RoomSummary } from '../shared/protocol';
import { DIFFICULTIES, REALMS } from '../shared/realms';
import { ClassId } from '../shared/types';
import { analytics } from './analytics';
import { audio } from './audio';
import { AuthPanel } from './authui';
import { textWidth, wrapText } from './gfx/font';
import { blitHero, heroFrame, skillIcon } from './gfx/sprites';
import { input } from './input';
import { GearLab } from './lab';
import { api, ApiError, HeroSummary } from './net';
import { C, ui } from './ui';

export interface ScreenHost {
  /** Starts the game in a room. */
  enterRoom(code: string, hero: HeroSummary): void;
  inRoom(): boolean;
}

export type ScreenId = 'title' | 'heroes' | 'create' | 'lobby' | 'lab';

interface Star {
  a: number;
  d: number;
  v: number;
  c: string;
}

const LOGO = 'RIFT CRAWL';

/** Cuts a line that's too wide for the space. */
function fitText(s: string, w: number): string {
  if (textWidth(s) <= w) return s;
  let cut = s;
  while (cut && textWidth(`${cut}...`) > w) cut = cut.slice(0, -1);
  return `${cut}...`;
}

export class Screens {
  id: ScreenId = 'title';
  private stars: Star[] = [];
  private heroes: HeroSummary[] = [];
  private heroesLoaded = false;
  private selected: HeroSummary | null = null;
  private rooms: RoomSummary[] = [];
  private roomsAt = 0;
  private busy = false;
  private error = '';
  private errorAt = 0;
  private createCls: ClassId = 'reaver';
  private createName = '';
  private roomName = '';
  private roomCode = '';
  private roomDiff = 0;
  private roomPublic = true;
  private confirmDelete: string | null = null;
  private time = 0;
  private autoRoom: string | null = null;
  private fatalNote = '';
  private lab: GearLab | null = null;
  private email: string | null = null;
  private panel: AuthPanel;
  private trackedScreen: ScreenId | null = null;

  constructor(private host: ScreenHost) {
    this.panel = new AuthPanel({
      // autoRoom and the note stay as they are, so a sign-in carries on to where the player was going.
      signedIn: () => void this.loadHeroes(),
      signedOut: (note) => this.askToSignIn(note),
      accountChanged: (email) => (this.email = email),
      otherTab: () => {
        if (this.host.inRoom() || this.id === 'title' || this.id === 'lab') return;
        this.panel.close();
        this.show('heroes', this.fatalNote);
      },
    });
    const palette = ['#b890fc', '#6844fc', '#3cbcfc', '#fcfcfc', '#d800cc', '#f8d878'];
    for (let i = 0; i < 220; i++) {
      this.stars.push({ a: Math.random() * 6.283, d: Math.random(), v: 0.08 + Math.random() * 0.25, c: palette[i % palette.length] });
    }
    const params = new URLSearchParams(location.search);
    const room = params.get('room');
    if (room && /^[A-Za-z0-9]{4,8}$/.test(room)) this.autoRoom = room.toUpperCase();
    if (params.has('lab')) this.id = 'lab';
    // Back from a room that closed for an update: one step away from rejoining it.
    const updated = params.get('updated');
    if (updated && /^[A-Za-z0-9]{4,8}$/.test(updated)) {
      this.autoRoom = updated.toUpperCase();
      this.show('heroes', `Room ${this.autoRoom} updated. Progress saved. Play to rejoin.`);
      try {
        const u = new URL(location.href);
        u.searchParams.delete('updated');
        history.replaceState(null, '', u);
      } catch {
        /* cosmetic */
      }
    }
  }

  /** Back to the hero list, e.g. after leaving a game. */
  show(id: ScreenId, note = ''): void {
    this.id = id;
    this.fatalNote = note;
    this.error = '';
    this.heroesLoaded = false;
    this.rooms = [];
    this.roomsAt = 0;
    if (id === 'heroes') void this.loadHeroes();
  }

  /** A room said this session is over. Signing in again leads straight back to it. */
  signedOut(room: string): void {
    this.autoRoom = room;
    this.askToSignIn('You were signed out. Sign in to carry on.');
  }

  private askToSignIn(note = ''): void {
    analytics.reset();
    this.id = 'heroes';
    this.heroesLoaded = false;
    this.email = null;
    ui.focusId = null;
    this.panel.open(note);
  }

  private fail(e: unknown): void {
    if (e instanceof ApiError && e.status === 401) {
      this.askToSignIn();
      return;
    }
    analytics.track('request_failed', { screen: this.id, status: e instanceof ApiError ? e.status : undefined });
    this.error = e instanceof Error ? e.message : String(e);
    this.errorAt = performance.now();
    audio.play('deny');
  }

  private async loadHeroes(): Promise<void> {
    try {
      const { heroes, email, accountId } = await api.heroes();
      this.heroes = heroes;
      this.email = email;
      if (accountId) analytics.identify(accountId, { hero_count: heroes.length, top_hero_level: Math.max(0, ...heroes.map((h) => h.level)) });
      this.heroesLoaded = true;
      if (!this.heroes.length) {
        this.id = 'create';
      } else {
        // The server lists the most recently played first, so this follows the player across devices.
        this.selected = this.heroes[0];
      }
    } catch (e) {
      this.fail(e);
    }
  }

  private async loadRooms(): Promise<void> {
    this.roomsAt = performance.now();
    try {
      this.rooms = await api.rooms();
    } catch (e) {
      // The list is a convenience: leave what's on screen, unless it means the session is gone.
      if (e instanceof ApiError && e.status === 401) this.fail(e);
    }
  }

  draw(W: number, H: number, dt: number): void {
    this.time += dt;
    input.textMode = false;
    // Screens change in many places, but all of them come through here.
    if (this.id !== this.trackedScreen) {
      this.trackedScreen = this.id;
      analytics.track('screen_viewed', { screen: this.id });
    }
    this.drawBackdrop(W, H, dt);
    switch (this.id) {
      case 'title':
        this.drawTitle(W, H);
        break;
      case 'heroes':
        this.drawHeroes(W, H);
        break;
      case 'create':
        this.drawCreate(W, H);
        break;
      case 'lab':
        this.lab ??= new GearLab();
        if (this.lab.draw(W, H, dt)) this.show('heroes');
        break;
      case 'lobby':
        this.drawLobby(W, H);
        break;
    }
    if (this.error && performance.now() - this.errorAt < 6000) {
      const w = textWidth(this.error) + 14;
      ui.rect((W - w) >> 1, H - 18, w, 13, '#000000');
      ui.rect(((W - w) >> 1) + 1, H - 17, w - 2, 11, '#600000');
      ui.text(this.error, W >> 1, H - 15, C.white, { align: 'center' });
    }
    ui.flushTooltip();
  }

  private drawBackdrop(W: number, H: number, dt: number): void {
    const g = ui.ctx;
    g.fillStyle = C.bg;
    g.fillRect(0, 0, W, H);
    const cx = W >> 1;
    const cy = Math.round(H * 0.42); // In the gap between the title logo and the tagline.
    const maxD = Math.hypot(W, H) * 0.6;
    for (const s of this.stars) {
      s.d += s.v * dt * (0.4 + s.d);
      if (s.d > 1) s.d = 0.02;
      s.a += dt * 0.12 * (1.3 - s.d);
      const dd = s.d * s.d * maxD;
      const x = cx + Math.cos(s.a) * dd;
      const y = cy + Math.sin(s.a) * dd * 0.7;
      g.fillStyle = s.d < 0.2 ? '#403060' : s.c;
      const size = s.d > 0.7 ? 2 : 1;
      g.fillRect(Math.round(x), Math.round(y), size, size);
    }
    // The maw at the center of it all.
    const r = 16 + Math.sin(this.time * 2) * 1.5;
    for (let i = 0; i < 90; i++) {
      const a = (i / 90) * 6.283 + this.time * 0.8;
      const rr = r + Math.sin(a * 5 + this.time * 3) * 2;
      g.fillStyle = i % 2 ? '#d800cc' : '#6844fc';
      g.fillRect(Math.round(cx + Math.cos(a) * rr), Math.round(cy + Math.sin(a) * rr * 0.7), 2, 1);
    }
    g.fillStyle = '#000000';
    for (let dy = -9; dy <= 9; dy++) {
      const span = Math.floor(Math.sqrt(Math.max(0, 196 - dy * dy * 2.4)));
      g.fillRect(cx - span, cy + dy, span * 2 + 1, 1);
    }
  }

  private drawLogo(W: number, y: number, scale = 4): void {
    const w = textWidth(LOGO, scale);
    const x0 = (W - w) >> 1;
    const flash = Math.floor(this.time * 2) % 8 === 0;
    // Kept dark: a brighter shadow fills the letters' counters and blurs them.
    ui.text(LOGO, x0 + scale, y + scale, '#2c0620', { scale, shadow: null });
    for (let i = 0; i < LOGO.length; i++) {
      const col = i % 2 === 0 ? '#f8b800' : '#f87800';
      ui.text(LOGO[i], x0 + i * 6 * scale, y, flash && i === Math.floor(this.time * 8) % LOGO.length ? C.white : col, { scale, shadow: null });
    }
  }

  private drawTitle(W: number, H: number): void {
    this.drawLogo(W, Math.round(H * 0.18), Math.min(5, Math.floor(W / 80)));
    ui.text('An eight-player action RPG', W >> 1, Math.round(H * 0.5), C.text, { align: 'center' });
    ui.text('The warp tore the world open. Crawl the rifts. Seal the wound.', W >> 1, Math.round(H * 0.56), C.dim, { align: 'center' });
    const blink = Math.floor(this.time * 1.8) % 2 === 0;
    if (blink) ui.text('PRESS ANY KEY', W >> 1, Math.round(H * 0.68), C.goldL, { align: 'center', scale: 2 });
    ui.text('M mute', W - 4, H - 11, C.dim, { align: 'right' });
    ui.text(`${REALMS.length} realms   6 classes   48 skills`, 4, H - 11, C.dim);
    // Above the line of text at the bottom, which reaches the middle on a narrow screen.
    // Before the check below, as clicking this mustn't also count as "any key".
    if (ui.button((W >> 1) - 30, H - 28, 60, 13, 'Privacy')) {
      analytics.track('privacy_policy_opened', { from: 'title' });
      // This runs in the frame after the click, which a browser may treat as a popup.
      if (!window.open(PRIVACY_URL, '_blank')) location.assign(PRIVACY_URL);
    } else if (input.lmbPressed || input.pressed.size > 0) {
      audio.play('click');
      this.show('heroes');
    }
  }

  private drawHeroes(W: number, H: number): void {
    this.drawLogo(W, 8, 2);
    const pw = Math.min(W - 16, 340);
    const ph = H - 40;
    const x = (W - pw) >> 1;
    const y = 30;
    ui.panel(x, y, pw, ph, 'Choose Your Hero');
    if (!this.heroesLoaded) {
      ui.text('Loading...', W >> 1, y + 30, C.dim, { align: 'center' });
      return;
    }
    if (this.fatalNote) ui.text(this.fatalNote, W >> 1, y + 15, C.orange, { align: 'center' });
    const rowH = 22;
    const listTop = y + (this.fatalNote ? 26 : 16);
    const cols = pw > 300 ? 2 : 1;
    const cw = Math.floor((pw - 16) / cols);
    const maxRows = Math.floor((ph - 62) / rowH);
    this.heroes.slice(0, cols * maxRows).forEach((h, i) => {
      const cx = x + 8 + (i % cols) * cw;
      const cy = listTop + Math.floor(i / cols) * rowH;
      const sel = this.selected?.id === h.id;
      const hot = ui.hit(cx, cy, cw - 2, rowH - 2);
      ui.rect(cx, cy, cw - 2, rowH - 2, sel ? C.goldD : hot ? C.hot : '#141014');
      const cls = CLASSES[h.cls];
      blitHero(ui.ctx, heroFrame(cls.look, 0, sel ? (Math.floor(this.time * 4) % 2) + 1 : 0), cx + 1, cy + 1);
      ui.text(h.name, cx + 19, cy + 2, sel ? C.white : C.goldL, { shadow: null });
      ui.text(`${cls.name} ${h.level}`, cx + 19, cy + 11, sel ? C.goldL : C.dim, { shadow: null });
      if (hot && input.lmbPressed) {
        if (sel) this.proceed();
        else {
          this.selected = h;
          this.confirmDelete = null;
          audio.play('click');
        }
      }
    });
    const by = y + ph - 22;
    if (ui.button(4, H - 17, 56, 13, 'Gear Lab')) this.id = 'lab';
    const accountW = 50;
    ui.text(fitText(this.email ?? '', pw - 22 - accountW), x + 9, by - 10, C.dim, { shadow: null });
    if (ui.button(x + pw - 8 - accountW, by - 13, accountW, 11, 'Account')) this.panel.openAccount(this.email ?? '');
    if (ui.button(x + 8, by, 80, 15, 'New Hero', { disabled: this.heroes.length >= 12 })) {
      this.createName = '';
      this.id = 'create';
    }
    const sel = this.selected;
    if (sel) {
      const confirming = this.confirmDelete === sel.id;
      if (ui.button(x + 92, by, 80, 15, confirming ? 'Really?' : 'Delete', { col: C.orange })) {
        if (confirming) {
          this.confirmDelete = null;
          void api.deleteHero(sel.id).then(() => {
            analytics.track('hero_deleted', { class: sel.cls, level: sel.level });
            this.selected = null;
            return this.loadHeroes();
          }).catch((e) => this.fail(e));
        } else {
          this.confirmDelete = sel.id;
        }
      }
      if (ui.button(x + pw - 88, by, 80, 15, 'Play', { col: C.goldL })) this.proceed();
    }
    if (input.pressed.has('Enter') && sel) this.proceed();
    if (input.pressed.has('Escape')) this.id = 'title';
  }

  private proceed(): void {
    const h = this.selected;
    if (!h) return;
    if (this.autoRoom) {
      const code = this.autoRoom;
      this.autoRoom = null;
      analytics.track('room_join_started', { via: 'link_or_rejoin', class: h.cls, level: h.level });
      this.host.enterRoom(code, h);
      return;
    }
    this.id = 'lobby';
    this.roomsAt = 0;
    this.roomName = `${h.name}'s Party`.slice(0, 20);
  }

  private drawCreate(W: number, H: number): void {
    const ph = H - 12;
    const pw = Math.min(W - 8, 440);
    const x = (W - pw) >> 1;
    const y = 6;
    ui.panel(x, y, pw, ph, 'Create a Hero');
    const cardW = Math.floor((pw - 16) / 3);
    const cardH = 34;
    CLASS_IDS.forEach((id, i) => {
      const cls = CLASSES[id];
      const cx = x + 8 + (i % 3) * cardW;
      const cy = y + 15 + Math.floor(i / 3) * cardH;
      const sel = this.createCls === id;
      const hot = ui.hit(cx, cy, cardW - 2, cardH - 2);
      ui.rect(cx, cy, cardW - 2, cardH - 2, sel ? C.goldD : hot ? C.hot : '#141014');
      const f = sel ? (Math.floor(this.time * 5) % 2) + 1 : 0;
      blitHero(ui.ctx, heroFrame(cls.look, 0, f), cx + 2, cy + 7);
      ui.text(cls.name, cx + 20, cy + 5, sel ? C.white : C.goldL, { shadow: null });
      const role = cls.role;
      for (const [li, line] of wrapText(role, cardW - 24).slice(0, 2).entries()) ui.text(line, cx + 20, cy + 14 + li * 9, sel ? C.goldL : C.dim, { shadow: null });
      if (hot && input.lmbPressed) {
        this.createCls = id;
        audio.play('click');
      }
    });
    const cls = CLASSES[this.createCls];
    let dy = y + 15 + cardH * 2 + 3;
    ui.text(cls.title, x + 10, dy, C.goldL);
    dy += 10;
    for (const line of wrapText(cls.desc, pw - 20).slice(0, 3)) {
      ui.text(line, x + 10, dy, C.text);
      dy += 9;
    }
    dy += 2;
    // Show the whole kit; it's the most useful thing on this screen.
    ui.text('Skills:', x + 10, dy, C.dim);
    dy += 9;
    const per = Math.max(1, Math.min(8, Math.floor((pw - 20) / 19)));
    cls.skills.forEach((k, i) => {
      const def = SKILLS[k];
      const ix = x + 10 + (i % per) * 19;
      const iy = dy + Math.floor(i / per) * 19;
      ui.rect(ix, iy, 18, 18, '#000000');
      ui.ctx.drawImage(skillIcon(def.icon), ix + 1, iy + 1);
      if (ui.hit(ix, iy, 18, 18)) {
        ui.tooltip(() => {
          const lines: { s: string; c?: string }[] = [{ s: def.name, c: C.goldL }, { s: `Unlocks at level ${def.tier}`, c: C.dim }];
          for (const l of wrapText(def.desc, 190)) lines.push({ s: l });
          for (const l of def.info(1)) lines.push({ s: l, c: C.cyan });
          ui.textBox(lines);
        });
      }
    });
    const statX = x + pw - 96;
    const s = cls.stats;
    ui.text(`Might    ${s.str}`, statX, dy - 9, C.text);
    ui.text(`Finesse  ${s.dex}`, statX, dy, C.text);
    ui.text(`Vitality ${s.vit}`, statX, dy + 9, C.text);
    ui.text(`Spirit   ${s.ene}`, statX, dy + 18, C.text);
    // With room to spare, spell out the kit and when each piece unlocks.
    const fyTop = y + ph - 26;
    let ky = dy + 29;
    const half = Math.floor((pw - 20) / 2);
    cls.skills.forEach((k, i) => {
      const row = ky + (i >> 1) * 9;
      if (row + 8 >= fyTop) return;
      const def = SKILLS[k];
      ui.text(`${def.name}`, x + 10 + (i % 2) * half, row, C.goldL);
      ui.text(`${def.tier}`, x + 10 + (i % 2) * half + half - 14, row, C.dim, { align: 'right' });
    });
    ky += 0;

    const fy = y + ph - 22;
    ui.text('Name', x + 10, fy + 3, C.goldL);
    ui.focusId = ui.focusId ?? 'heroName';
    this.createName = ui.field('heroName', x + 38, fy, 110, this.createName, { max: 14, placeholder: 'Your name' });
    const valid = /^[A-Za-z0-9 _-]{2,14}$/.test(this.createName.trim());
    if (ui.button(x + pw - 88, fy - 1, 80, 15, this.busy ? 'Forging...' : 'Create', { disabled: !valid || this.busy, col: C.goldL }) || (valid && input.pressed.has('Enter'))) {
      this.busy = true;
      api.createHero(this.createName.trim(), this.createCls).then((h) => {
        analytics.track('hero_created', { class: h.cls, is_first_hero: this.heroes.length === 0 });
        this.busy = false;
        this.selected = h;
        ui.focusId = null;
        return this.loadHeroes().then(() => {
          this.selected = h;
          this.id = 'heroes';
          this.proceed();
        });
      }).catch((e) => {
        this.busy = false;
        this.fail(e);
      });
    }
    if (this.heroes.length) {
      if (ui.button(x + pw - 170, fy - 1, 78, 15, 'Back')) {
        ui.focusId = null;
        this.id = 'heroes';
      }
    } else if (ui.button(x + pw - 170, fy - 1, 78, 15, 'Account')) {
      // With no hero list to go back to, this is the only way to sign out, or to get out of a mistyped address.
      this.panel.openAccount(this.email ?? '');
    }
    if (input.pressed.has('Escape') && this.heroes.length) {
      ui.focusId = null;
      this.id = 'heroes';
    }
  }

  private drawLobby(W: number, H: number): void {
    this.drawLogo(W, 6, 2);
    if (performance.now() - this.roomsAt > 5000 || !this.roomsAt) void this.loadRooms();
    const h = this.selected!;
    const y = 26;
    const ph = H - 32;
    const leftW = Math.min(250, W - 180);
    const lx = 6;
    ui.panel(lx, y, leftW, ph, 'Open Rooms');
    const rowH = 15;
    const list = this.rooms;
    const maxRows = Math.floor((ph - 26) / rowH);
    if (!list.length) {
      ui.text('No open rooms yet.', lx + leftW / 2, y + 30, C.dim, { align: 'center' });
      ui.text('Create the first one!', lx + leftW / 2, y + 40, C.dim, { align: 'center' });
    }
    list.slice(0, maxRows).forEach((r, i) => {
      const ry = y + 15 + i * rowH;
      const full = r.players >= r.max;
      const label = r.name.length > 20 ? `${r.name.slice(0, 19)}.` : r.name;
      if (ui.button(lx + 6, ry, leftW - 12, rowH - 1, '', { disabled: full })) {
        analytics.track('room_join_started', { via: 'list', class: h.cls, level: h.level, room_players: r.players, difficulty: DIFFICULTIES[r.diff]?.name });
        this.host.enterRoom(r.code, h);
      }
      ui.text(label, lx + 10, ry + 3, full ? C.dim : C.goldL, { shadow: null });
      const info = `${r.players}/${r.max}  ${DIFFICULTIES[r.diff]?.name.slice(0, 1) ?? 'N'} ~${r.avgLevel}`;
      ui.text(info, lx + leftW - 10, ry + 3, C.text, { align: 'right', shadow: null });
    });

    const rx = lx + leftW + 4;
    const rw = W - rx - 6;
    ui.panel(rx, y, rw, 118, 'Create Room');
    ui.text('Name', rx + 8, y + 18, C.text);
    this.roomName = ui.field('roomName', rx + 8, y + 27, rw - 16, this.roomName, { max: 24 });
    ui.text('Difficulty', rx + 8, y + 44, C.text);
    const dw = Math.floor((rw - 16) / 3);
    DIFFICULTIES.forEach((d, i) => {
      if (ui.button(rx + 8 + i * dw, y + 53, dw - 1, 13, d.name.slice(0, dw > 50 ? 9 : 5), { active: this.roomDiff === i })) this.roomDiff = i;
    });
    if (ui.button(rx + 8, y + 69, rw - 16, 13, this.roomPublic ? 'Listed publicly' : 'Private: code only')) this.roomPublic = !this.roomPublic;
    if (ui.button(rx + 8, y + 88, rw - 16, 17, this.busy ? 'Opening...' : 'Create & Enter', { col: C.goldL, disabled: this.busy })) {
      this.busy = true;
      api.createRoom(this.roomName.trim() || `${h.name}'s Party`, this.roomDiff, this.roomPublic).then((res) => {
        this.busy = false;
        ui.focusId = null;
        analytics.track('room_created', { difficulty: DIFFICULTIES[res.diff]?.name, is_public: this.roomPublic, class: h.cls, level: h.level });
        this.host.enterRoom(res.code, h);
      }).catch((e) => {
        this.busy = false;
        this.fail(e);
      });
    }

    const jy = y + 122;
    const jh = Math.max(60, ph - 122);
    ui.panel(rx, jy, rw, jh, 'Join by Code');
    this.roomCode = ui.field('roomCode', rx + 8, jy + 16, rw - 16, this.roomCode, { max: 8, upper: true, placeholder: 'ROOM CODE' });
    const validCode = /^[A-Z0-9]{4,8}$/.test(this.roomCode);
    if (ui.button(rx + 8, jy + 33, (rw - 18) >> 1, 15, 'Join', { disabled: !validCode, col: C.goldL })) {
      ui.focusId = null;
      analytics.track('room_join_started', { via: 'code', class: h.cls, level: h.level });
      this.host.enterRoom(this.roomCode, h);
    }
    if (ui.button(rx + 10 + ((rw - 18) >> 1), jy + 33, (rw - 18) >> 1, 15, 'Back')) {
      ui.focusId = null;
      this.show('heroes');
    }
    if (input.pressed.has('Escape')) {
      ui.focusId = null;
      this.show('heroes');
    }
  }
}
